<template>
  <ionic-vue-page v-bind:title="screenTitle" v-bind:color="'dark'" v-bind:refresh="refresh">
    <ion-toolbar slot="header" color="primary">
      <ion-searchbar
        placeholder="Search resources by keyword"
        @ionChange="search"
        v-bind:value="searchTerm"
        @ionInput="searchTerm = $event.target.value"
        @ionClear="searchCleared()"
      ></ion-searchbar>
    </ion-toolbar>

    <error-message v-if="error" v-bind:message="error"></error-message>

    <div class="topics">
      <div class="ion-padding">
        <ion-button @click="presentActionSheet" expand="block" color="primary">
          <font-awesome-icon slot="start" icon="filter"></font-awesome-icon>
          <span class="m-l-m">Filter By Topic</span>
        </ion-button>
      </div>
    </div>

    <skeleton-resource-card v-if="resources.length === 0 && savedResources.length === 0"></skeleton-resource-card>

    <div
      class="ion-padding ion-text-center"
      v-if="resources.length === 0 && savedResources.length > 0"
    >
      <p>No results found for {{ searchTerm }}</p>
    </div>

    <ion-card v-else v-for="(resource, index) in resources" v-bind:key="index" color="light">
      <ion-card-header>
        <ion-card-title class="h-f-b">{{ resource.title }}</ion-card-title>
        <ion-card-subtitle class="h-f-m">{{ resource.created_at | formatTime("MMM Do, YYYY") }}</ion-card-subtitle>
      </ion-card-header>

      <ion-card-content>
        <div v-if="checkMedia(resource.media) === 'mp4'">
          <video width="100%;" controls preload="none" :poster="resource.thumbnail">
            <source v-bind:src="resource.media" type="video/mp4" />
            <p>Your browser does not support HTML5 video.</p>
          </video>
        </div>

        <div
          v-if="checkMedia(resource.media) === 'jpg' || checkMedia(resource.media) === 'png' || checkMedia(resource.media) === 'gif'"
        >
          <!-- <img tappable @click="openImage" v-bind:src="resource.media" /> -->
          <lazy-image v-if="resource.media" v-bind:source="resource.media"></lazy-image>
        </div>

        <resource-description :description="resource.description"></resource-description>

        <div v-if="checkMedia(resource.media) === 'pdf'">
          <ion-button @click="openPdf(resource.media)" expand="full" size="large">View</ion-button>
        </div>
      </ion-card-content>

      <ion-card-footer v-if="resource.tags.length > 0">
        <div class="ion-padding">
          <span class="h-f-m">Tagged:</span>
          <ion-chip
            v-for="(tag, index) in resource.tags"
            v-bind:key="index"
            color="tertiary"
            outline="true"
            @click="filterByTopic(tag)"
          >
            <ion-label>{{ tag }}</ion-label>
          </ion-chip>
        </div>
      </ion-card-footer>
    </ion-card>
  </ionic-vue-page>
</template>

<script>
import { Plugins } from "@capacitor/core";
const { Browser } = Plugins;
import resourcesApiService from "./../services/ResourcesApi";
import moment from "moment";
import LazyImage from "./../components/LazyImage";
import mixins from "./../mixins/mixins";
import SkeletonResourceCard from "./../components/SkeletonResourceCard";
import ResourceDescription from './../components/ResourceDescription';

export default {
  name: "Resources",
  mixins: [mixins],
  components: {
    "lazy-image": LazyImage,
    "skeleton-resource-card": SkeletonResourceCard,
    "resource-description": ResourceDescription
  },
  data() {
    return {
      screenTitle: "Resources",
      resources: [],
      savedResources: [],
      error: false,
      searchTerm: "",
      selectedOption: null,
    };
  },

  async mounted() {
    const loading = await this.$ionic.loadingController.create({
      message: "Loading resources..."
    });
    loading.present();

    try {
      const resources = await resourcesApiService.get();
      this.resources = resources;
      this.savedResources = resources;
    } catch (error) {
      this.error = error;
    } finally {
      loading.dismiss();
    }
  },

  methods: {
    checkMedia(media) {
      if (media === null) {
        return;
      }

      return media.slice(((media.lastIndexOf(".") - 1) >>> 0) + 2);
    },

    async openPdf(pdf) {
      await Browser.open({ url: pdf });
    },

    filterByTopic(topic) {
      this.resources = this.savedResources
        .filter(element => element.tags.some(subElement => subElement == topic))
        .map(element => {
          return Object.assign({}, element, {
            subElements: element.tags.filter(subElement => subElement == topic)
          });
        });
    },

    getTopics() {
      const topics = [];

      if (this.savedResources.length === 0) {
        return;
      }

      this.savedResources.map(resource => {
        const tags = resource.tags;
        if (tags) {
          tags.map(tag => {
            if (topics.indexOf(tag) === -1) {
              topics.push(tag);
            }
          });
        }
      });

      topics.sort();
      return topics;
    },

    clearFilter() {
      this.resources = this.savedResources;
    },

    search() {
      if (this.searchTerm) {
        this.resources = this.savedResources.filter(item => {
          return (
            item.title.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1
          );
        });
      } else {
        this.clearFilter();
      }
    },

    searchCleared() {
      this.clearFilter();
    },

    createButtons() {
      const clearButton = {
        text: "All",
        handler: () => {
          this.clearFilter();
          return true;
        }
      };

      const buttons = [clearButton];
      const topics = this.getTopics();

      topics.forEach(topic => {
        const button = {
          text: topic,
          handler: () => {
            this.filterByTopic(topic);
            return true;
          }
        };
        buttons.push(button);
      });

      return buttons;
    },

    presentActionSheet() {
      return this.$ionic.actionSheetController
        .create({
          header: "Select a topic",
          buttons: this.createButtons()
        })
        .then(a => a.present());
    },

    async refresh(event) {
      this.resources = [];
      this.savedResources = [];

      try {
        const resources = await resourcesApiService.get();
        this.resources = resources;
        this.savedResources = resources;
      } catch (error) {
        this.error = error;
      } finally {
        event.target.complete();
      }
    }
  },

  filters: {
    formatTime(value, format) {
      if (!value) {
        return;
      }

      return moment(value).format(format);
    }
  }
};
</script>